import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import { askPdfOrXlsx } from '../utils/pdfPrint';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3, Settings, Save } from 'lucide-react';

const DailyOperationsReports = () => {
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const [reportData, setReportData] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [groupByAgency, setGroupByAgency] = useState(true);
  const [groupByService, setGroupByService] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailOverride, setEmailOverride] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  useEffect(() => {
    fetchAgencies();
    fetchEmployees();
  }, []);

  const fetchAgencies = async () => {
    // /api/agencies is restricted to super_admin + general_accountant.
    // For other roles the call will 403 — that's expected and not a bug.
    // We silently ignore the failure and leave the list empty so the page
    // still renders (their reports are scoped to their own agency anyway).
    if (user?.role && !['super_admin', 'general_accountant'].includes(user.role)) {
      return;
    }
    try {
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAgencies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      setAgencies([]);
    }
  };

  const fetchEmployees = async () => {
    // Populate employee dropdown so manager/accountant can filter the
    // report to "what did this specific employee do today".
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEmployees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Staff role can't list users — that's OK, the dropdown stays empty
      // and the report is scoped to their own activity anyway.
      console.warn('Employees list not available for this role');
      setEmployees([]);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      console.log('=== GENERATING REPORT ===');
      const params = new URLSearchParams({
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        group_by_agency: groupByAgency.toString(),
        group_by_service: groupByService.toString()
      });

      // Add agency filter if specific agency is selected
      if (selectedAgency !== 'all') {
        params.append('agency_ids', selectedAgency);
      }
      if (selectedEmployee !== 'all') {
        params.append('employee_id', selectedEmployee);
      }

      console.log('Report params:', params.toString());
      const apiUrl = `${API}/reports/daily-operations?${params}`;
      console.log('Report API URL:', apiUrl);

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('Report response:', response.data);
      console.log('Report response status:', response.status);
      
      if (response.data) {
        setReportData(response.data);
        console.log('✅ Report data set successfully');
      } else {
        console.log('❌ No report data received');
        alert('لا توجد بيانات للفترة المحددة');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert('خطأ في إنتاج التقرير: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReport = async () => {
    if (!reportData) {
      alert('يجب إنشاء التقرير أولاً');
      return;
    }
    // Use the backend's branded PDF endpoint (premium_gold template) so
    // the printed sheet matches every other document the agency issues.
    // The shared FormatPickerDialog will ask PDF vs Excel.
    try {
      const params = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        group_by_agency: groupByAgency.toString(),
      };
      if (selectedAgency !== 'all') params.agency_ids = selectedAgency;
      if (selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      await askPdfOrXlsx(`${API}/reports/daily-operations/print`, params);
    } catch (error) {
      console.error('Error printing report:', error);
      alert('خطأ في طباعة التقرير: ' + error.message);
    }
  };

  const handleEmailReport = async () => {
    // Opens the email dialog. The dialog calls submitEmailReport() on confirm.
    if (!reportData) {
      alert('يجب إنشاء التقرير أولاً');
      return;
    }
    setEmailOverride('');
    setEmailResult(null);
    setEmailDialogOpen(true);
  };

  const submitEmailReport = async () => {
    setEmailSending(true);
    setEmailResult(null);
    try {
      const body = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        group_by_agency: groupByAgency,
      };
      if (selectedAgency !== 'all') body.agency_ids = selectedAgency;
      if (selectedEmployee !== 'all') body.employee_id = selectedEmployee;
      if (emailOverride && emailOverride.trim()) body.to_email = emailOverride.trim();

      const resp = await axios.post(`${API}/reports/daily-operations/email`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setEmailResult({ ok: true, message: resp.data?.message || 'تم الإرسال بنجاح', to: resp.data?.to });
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      setEmailResult({ ok: false, message: detail });
    } finally {
      setEmailSending(false);
    }
  };

  const createHTMLReport = (data) => {
    const totals = data.grand_totals || data.agencies_data?.[0]?.totals || {};
    
    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>تقرير العمليات اليومية</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap');
          body {
            font-family: 'Tajawal', 'Arial', 'Tahoma', sans-serif;
            margin: 20px;
            direction: rtl;
            background-color: white;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .title {
            font-size: 24px;
            color: #1e40af;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .subtitle {
            font-size: 18px;
            color: #059669;
            margin-bottom: 15px;
          }
          .period {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 5px;
          }
          .summary-section {
            background-color: #f8fafc;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 30px;
          }
          .summary-title {
            font-size: 18px;
            color: #1e40af;
            font-weight: bold;
            margin-bottom: 15px;
            border-bottom: 1px solid #cbd5e1;
            padding-bottom: 8px;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
          }
          .summary-item {
            background-color: white;
            padding: 15px;
            border-radius: 6px;
            border-left: 4px solid #3b82f6;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .summary-label {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 5px;
          }
          .summary-value {
            font-size: 20px;
            font-weight: bold;
            color: #1f2937;
          }
          .agencies-section {
            margin-top: 30px;
          }
          .agency-card {
            background-color: #fefefe;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .agency-name {
            font-size: 16px;
            font-weight: bold;
            color: #dc2626;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid #fecaca;
          }
          .agency-stats {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
            margin-top: 15px;
          }
          .agency-stat {
            text-align: center;
            padding: 10px;
            background-color: #f9fafb;
            border-radius: 4px;
          }
          .stat-label {
            font-size: 12px;
            color: #6b7280;
            margin-bottom: 5px;
          }
          .stat-value {
            font-size: 16px;
            font-weight: bold;
            color: #374151;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            color: #9ca3af;
            font-size: 12px;
            border-top: 1px solid #e5e7eb;
            padding-top: 20px;
          }
          @media print {
            body { margin: 0; }
            .summary-grid { grid-template-columns: 1fr 1fr; }
            .agency-stats { grid-template-columns: repeat(2, 1fr); }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">🏢 نظام إدارة الوكالات السياحية</div>
          <div class="subtitle">📊 تقرير العمليات اليومية المفصل</div>
          <div class="period">📅 فترة التقرير: ${data.period || 'غير محددة'}</div>
          <div class="period">⏰ تاريخ الإنشاء: ${new Date().toLocaleDateString('ar-SA')} - ${new Date().toLocaleTimeString('ar-SA')}</div>
        </div>

        <div class="summary-section">
          <div class="summary-title">📈 ملخص التقرير العام</div>
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">📊 عدد العمليات الإجمالي</div>
              <div class="summary-value">${totals.operations_count || 0}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">💰 الإيرادات الإجمالية</div>
              <div class="summary-value">${(totals.total_revenue || 0).toLocaleString()} دج</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">🏷️ إجمالي التخفيضات</div>
              <div class="summary-value">${(totals.total_discounts || 0).toLocaleString()} دج</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">✅ صافي الإيرادات</div>
              <div class="summary-value" style="color: #059669;">${(totals.net_revenue || 0).toLocaleString()} دج</div>
            </div>
          </div>
        </div>

        ${data.agencies_data && data.agencies_data.length > 0 ? `
        <div class="agencies-section">
          <div class="summary-title">🏢 تفاصيل الوكالات</div>
          ${data.agencies_data.map(agency => `
            <div class="agency-card">
              <div class="agency-name">▶ ${agency.agency_name || 'وكالة غير محددة'}</div>
              <div class="agency-stats">
                <div class="agency-stat">
                  <div class="stat-label">العمليات</div>
                  <div class="stat-value">${agency.totals?.operations_count || 0}</div>
                </div>
                <div class="agency-stat">
                  <div class="stat-label">الإيرادات</div>
                  <div class="stat-value">${(agency.totals?.total_revenue || 0).toLocaleString()}</div>
                </div>
                <div class="agency-stat">
                  <div class="stat-label">التخفيضات</div>
                  <div class="stat-value">${(agency.totals?.total_discounts || 0).toLocaleString()}</div>
                </div>
                <div class="agency-stat">
                  <div class="stat-label">الصافي</div>
                  <div class="stat-value" style="color: #059669;">${(agency.totals?.net_revenue || 0).toLocaleString()}</div>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
        ` : ''}

        <div class="footer">
          <div>═══════════════════════════════════════════════════════════════</div>
          <div style="margin-top: 10px;">تم إنشاء هذا التقرير بواسطة نظام إدارة الوكالات السياحية</div>
          <div>📧 للدعم الفني يرجى التواصل مع قسم تقنية المعلومات</div>
        </div>
      </body>
      </html>
    `;
  };

  // Test function to create dummy report data
  const createTestReport = () => {
    console.log('Creating test report data...');
    const testData = {
      title: 'تقرير العمليات اليومية - اختبار',
      period: `من ${startDate.toLocaleDateString('ar-SA')} إلى ${endDate.toLocaleDateString('ar-SA')}`,
      total_operations: 5,
      total_amount: 125000,
      agencies_data: [
        {
          agency_name: 'وكالة سنهاجة الرئيسية',
          operations_count: 3,
          total_amount: 75000,
          operations: [
            { operation_no: 'OP-001', service_name: 'حجز طيران', amount: 25000, date: '2025-09-02' },
            { operation_no: 'OP-002', service_name: 'حجز فندق', amount: 30000, date: '2025-09-02' },
            { operation_no: 'OP-003', service_name: 'تأشيرة', amount: 20000, date: '2025-09-02' }
          ]
        }
      ],
      group_by_agency: true
    };
    
    console.log('Setting report data...', testData);
    setReportData(testData);
    console.log('Report data set successfully');
    setLoading(false); // Make sure loading is false
    alert('✅ تم إنشاء تقرير اختبار');
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <h1 className="text-2xl font-bold text-gray-900">{t('dailyOperationsReports')}</h1>

      <Card>
        <CardHeader>
          <CardTitle>إعدادات التقرير</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>من تاريخ</Label>
              <Input
                type="date"
                value={startDate.toISOString().split('T')[0]}
                onChange={(e) => setStartDate(new Date(e.target.value))}
              />
            </div>
            <div>
              <Label>إلى تاريخ</Label>
              <Input
                type="date"
                value={endDate.toISOString().split('T')[0]}
                onChange={(e) => setEndDate(new Date(e.target.value))}
              />
            </div>
          </div>

          {/* Agency Filter — only shown to multi-agency roles */}
          {agencies.length > 0 && (
            <div>
              <Label>فلتر الوكالة</Label>
              <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                <SelectTrigger>
                  <SelectValue placeholder="اختر الوكالة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">🌐 جميع الوكالات</SelectItem>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name} - {agency.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* 🆕 Employee Filter — lets managers/accountants see what a
              specific employee did in the selected period. */}
          {employees.length > 0 && (
            <div>
              <Label>فلتر الموظف</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="employee-filter-select">
                  <SelectValue placeholder="اختر الموظف" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">👥 جميع الموظفين</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name || emp.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="groupByAgency"
                checked={groupByAgency}
                onChange={(e) => setGroupByAgency(e.target.checked)}
              />
              <Label htmlFor="groupByAgency">تجميع حسب الوكالة</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="groupByService"
                checked={groupByService}
                onChange={(e) => setGroupByService(e.target.checked)}
              />
              <Label htmlFor="groupByService">تجميع حسب الخدمة</Label>
            </div>
          </div>

          <Button onClick={generateReport} disabled={loading} className="bg-brand-600 hover:bg-brand-700">
            {loading ? 'جاري الإنتاج...' : t('generateReport')}
          </Button>

          {/* Test Button - temporary */}
          <Button onClick={createTestReport} className="bg-yellow-600 hover:bg-yellow-700">
            🧪 إنشاء تقرير اختبار
          </Button>

          {/* Print Report Button */}
          {reportData && (
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={handlePrintReport} 
                disabled={loading}
                variant="outline" 
                className="bg-green-600 hover:bg-green-700 text-white border-green-600"
                data-testid="print-daily-report-btn"
              >
                🖨️ طباعة التقرير PDF
              </Button>
              <Button
                onClick={handleEmailReport}
                disabled={loading}
                variant="outline"
                className="bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600"
                data-testid="email-daily-report-btn"
              >
                📧 إرسال بالبريد للمدير
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {reportData && (
        <Card>
          <CardHeader>
            <CardTitle>{reportData.title}</CardTitle>
            <CardDescription>
              {reportData.period}
              {selectedAgency !== 'all' && (
                <span className="mr-2 text-brand-600">
                  • الوكالة: {agencies.find(a => a.id === selectedAgency)?.name}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* 🆕 Top summary — 4 KPI cards showing the full picture:
                collected revenue, transferred to admin, expenses, and the
                net balance ("الباقي") the employee still holds. */}
            {reportData.summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="daily-report-summary">
                <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4 text-center">
                  <div className="text-xs font-semibold text-green-800 mb-1">💰 إجمالي المداخيل</div>
                  <div className="text-2xl font-extrabold text-green-700" data-testid="summary-collected">
                    {Number(reportData.summary.total_collected || 0).toLocaleString()} دج
                  </div>
                </div>
                <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-4 text-center">
                  <div className="text-xs font-semibold text-blue-800 mb-1">🔄 التحويلات (مؤكدة)</div>
                  <div className="text-2xl font-extrabold text-blue-700" data-testid="summary-transferred">
                    {Number(reportData.summary.total_transferred_out || 0).toLocaleString()} دج
                  </div>
                </div>
                <div className="rounded-lg border-2 border-orange-200 bg-orange-50 p-4 text-center">
                  <div className="text-xs font-semibold text-orange-800 mb-1">🧾 المصاريف (معتمدة)</div>
                  <div className="text-2xl font-extrabold text-orange-700" data-testid="summary-expenses">
                    {Number(reportData.summary.total_expenses || 0).toLocaleString()} دج
                  </div>
                </div>
                <div className={`rounded-lg border-2 p-4 text-center ${
                  Number(reportData.summary.net_balance || 0) >= 0
                    ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'
                }`}>
                  <div className="text-xs font-semibold mb-1">
                    {Number(reportData.summary.net_balance || 0) >= 0 ? '🟡 الباقي / الرصيد الصافي' : '🔴 عجز'}
                  </div>
                  <div className={`text-2xl font-extrabold ${
                    Number(reportData.summary.net_balance || 0) >= 0 ? 'text-amber-700' : 'text-red-700'
                  }`} data-testid="summary-net-balance">
                    {Number(reportData.summary.net_balance || 0).toLocaleString()} دج
                  </div>
                </div>
              </div>
            )}

            {reportData.group_by_agency && Array.isArray(reportData.agencies_data) && reportData.agencies_data.length > 0 ? (
              <div className="space-y-6">
                {reportData.agencies_data.map((agency, index) => {
                  // Robust safe accessors — older docs and edge cases may have
                  // null / undefined values that would crash toLocaleString().
                  const tot = agency.totals || {};
                  const opsCount = tot.operations_count ?? 0;
                  const revenue  = Number(tot.total_revenue || 0);
                  const disc     = Number(tot.total_discounts || 0);
                  const net      = Number(tot.net_revenue || 0);
                  // services may be:
                  //  (a) Array of operation rows  (group_by_service=false)
                  //  (b) Array of service-groups  (group_by_service=true) — each item
                  //      has shape { service_name, service_type, operations:[], totals:{} }
                  //  (c) Object keyed by service_name (older backend variant)
                  let serviceGroups = [];   // [{service_name, operations:[], totals:{}}, ...]
                  let flatOperations = [];  // when no grouping
                  const isGroupedByService = !!reportData.group_by_service;
                  if (Array.isArray(agency.services)) {
                    if (isGroupedByService && agency.services.length > 0 && Array.isArray(agency.services[0]?.operations)) {
                      serviceGroups = agency.services;
                    } else {
                      flatOperations = agency.services;
                    }
                  } else if (agency.services && typeof agency.services === 'object') {
                    serviceGroups = Object.values(agency.services);
                  }
                  return (
                  <div key={agency.agency_id || index} className="border rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-4">{agency.agency_name || '—'}</h3>
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-brand-600">
                          {opsCount}
                        </div>
                        <div className="text-sm text-gray-600">العمليات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {revenue.toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">إجمالي الإيرادات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {disc.toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">إجمالي التخفيضات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {net.toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">صافي الإيرادات</div>
                      </div>
                    </div>

                    {/* === MODE B: grouped by service — render one collapsible
                        sub-section per service with its own table + totals === */}
                    {serviceGroups.length > 0 && (
                      <div className="mt-4 space-y-4">
                        {serviceGroups.map((svc, sIdx) => {
                          const st = svc.totals || {};
                          return (
                          <details key={sIdx} className="border rounded-lg bg-amber-50/40" open>
                            <summary className="cursor-pointer p-3 font-bold flex flex-wrap gap-2 items-center justify-between">
                              <span>📦 {svc.service_name || '—'}
                                {svc.service_type && (
                                  <span className="text-xs text-gray-500 mr-2 font-normal">
                                    ({svc.service_type})
                                  </span>
                                )}
                              </span>
                              <span className="text-xs text-gray-700 flex gap-3 flex-wrap">
                                <span>عمليات: <b>{st.operations_count ?? 0}</b></span>
                                <span className="text-green-700">إيراد: <b>{Number(st.total_revenue || 0).toLocaleString()} دج</b></span>
                                <span className="text-red-700">خصم: <b>{Number(st.total_discounts || 0).toLocaleString()} دج</b></span>
                                <span className="text-purple-700">صافي: <b>{Number(st.net_revenue || 0).toLocaleString()} دج</b></span>
                              </span>
                            </summary>
                            <div className="p-2 overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="text-right">رقم الوصل</TableHead>
                                    <TableHead className="text-right">التاريخ</TableHead>
                                    <TableHead className="text-right">العميل</TableHead>
                                    <TableHead className="text-right">السعر</TableHead>
                                    <TableHead className="text-right">التخفيض</TableHead>
                                    <TableHead className="text-right">الصافي</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(svc.operations || []).slice(0, 50).map((op, oi) => (
                                    <TableRow key={oi}>
                                      <TableCell className="text-right text-sm">{op.operation_no || '—'}</TableCell>
                                      <TableCell className="text-right text-sm">{op.date || '—'}</TableCell>
                                      <TableCell className="text-right text-sm">{op.client_name || '—'}</TableCell>
                                      <TableCell className="text-right text-sm">{Number(op.base_price || 0).toLocaleString()} دج</TableCell>
                                      <TableCell className="text-right text-sm">{Number(op.discount_amount || 0).toLocaleString()} دج</TableCell>
                                      <TableCell className="text-right text-sm font-semibold">{Number(op.final_price || 0).toLocaleString()} دج</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                              {(svc.operations || []).length > 50 && (
                                <p className="text-xs text-gray-500 mt-1">عرض أول 50 من {svc.operations.length}</p>
                              )}
                            </div>
                          </details>
                          );
                        })}
                      </div>
                    )}

                    {/* === MODE A: flat operations table (no service grouping) === */}
                    {flatOperations.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold mb-2">تفاصيل العمليات:</h4>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-right">رقم الوصل</TableHead>
                                <TableHead className="text-right">التاريخ</TableHead>
                                <TableHead className="text-right">العميل</TableHead>
                                <TableHead className="text-right">الخدمة</TableHead>
                                <TableHead className="text-right">السعر الأساسي</TableHead>
                                <TableHead className="text-right">التخفيض</TableHead>
                                <TableHead className="text-right">السعر النهائي</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {flatOperations.slice(0, 10).map((operation, opIndex) => (
                                <TableRow key={opIndex}>
                                  <TableCell className="text-right text-sm">{operation.operation_no || '—'}</TableCell>
                                  <TableCell className="text-right text-sm">{operation.date || '—'}</TableCell>
                                  <TableCell className="text-right text-sm">{operation.client_name || '—'}</TableCell>
                                  <TableCell className="text-right text-sm">{operation.service_name || '—'}</TableCell>
                                  <TableCell className="text-right text-sm">{Number(operation.base_price || 0).toLocaleString()} دج</TableCell>
                                  <TableCell className="text-right text-sm">{Number(operation.discount_amount || 0).toLocaleString()} دج</TableCell>
                                  <TableCell className="text-right text-sm font-semibold">{Number(operation.final_price || 0).toLocaleString()} دج</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          {flatOperations.length > 10 && (
                            <p className="text-sm text-gray-600 mt-2">
                              عرض أول 10 عمليات من إجمالي {flatOperations.length} عملية
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
                
                {reportData.grand_totals && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-4">
                      المجموع العام
                      {selectedAgency !== 'all' && (
                        <span className="text-sm text-gray-600 font-normal mr-2">
                          (الوكالة المحددة فقط)
                        </span>
                      )}
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-brand-600">
                          {reportData.grand_totals.operations_count ?? 0}
                        </div>
                        <div className="text-sm text-gray-600">إجمالي العمليات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {Number(reportData.grand_totals.total_revenue || 0).toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">إجمالي الإيرادات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {Number(reportData.grand_totals.total_discounts || 0).toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">إجمالي التخفيضات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {Number(reportData.grand_totals.net_revenue || 0).toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">صافي الإيرادات</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : reportData.group_by_agency && Array.isArray(reportData.agencies_data) && reportData.agencies_data.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <div className="text-5xl mb-3">📭</div>
                <p className="text-lg">لا توجد عمليات في النطاق الزمني المحدد</p>
                <p className="text-sm mt-1">جرّب توسيع المدى أو إزالة فلتر الوكالة</p>
              </div>
            ) : reportData.data ? (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">تفاصيل العمليات</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-right">رقم الوصل</TableHead>
                        <TableHead className="text-right">التاريخ</TableHead>
                        <TableHead className="text-right">الوكالة</TableHead>
                        <TableHead className="text-right">العميل</TableHead>
                        <TableHead className="text-right">الخدمة</TableHead>
                        <TableHead className="text-right">السعر الأساسي</TableHead>
                        <TableHead className="text-right">التخفيض</TableHead>
                        <TableHead className="text-right">السعر النهائي</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.data.map((operation, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-right text-sm">{operation.operation_no || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{operation.date || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{operation.agency_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{operation.client_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{operation.service_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{Number(operation.base_price || 0).toLocaleString()} دج</TableCell>
                          <TableCell className="text-right text-sm">{Number(operation.discount_amount || 0).toLocaleString()} دج</TableCell>
                          <TableCell className="text-right text-sm font-semibold">{Number(operation.final_price || 0).toLocaleString()} دج</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {reportData.totals && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-4">المجموع العام</h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-brand-600">
                          {reportData.totals.operations_count ?? 0}
                        </div>
                        <div className="text-sm text-gray-600">إجمالي العمليات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {Number(reportData.totals.total_revenue || 0).toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">إجمالي الإيرادات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {Number(reportData.totals.total_discounts || 0).toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">إجمالي التخفيضات</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {Number(reportData.totals.net_revenue || 0).toLocaleString()} دج
                        </div>
                        <div className="text-sm text-gray-600">صافي الإيرادات</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-600">
                لا توجد بيانات لعرضها
              </div>
            )}

            {/* 🆕 Transfers section — what the employee handed over to the
                admin / general accountant during the period. */}
            {reportData.transfers && reportData.transfers.rows && reportData.transfers.rows.length > 0 && (
              <div className="mt-8 border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30" data-testid="transfers-section">
                <h3 className="text-lg font-bold mb-3 text-blue-900 flex items-center gap-2">
                  💸 التحويلات المالية ({reportData.transfers.count})
                  <span className="text-sm font-normal text-blue-700 mr-auto">
                    المجموع المؤكد: <b>{Number(reportData.transfers.total || 0).toLocaleString()} دج</b>
                  </span>
                </h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-right">المرجع</TableHead>
                        <TableHead className="text-right">التاريخ</TableHead>
                        <TableHead className="text-right">الوكالة</TableHead>
                        <TableHead className="text-right">الموظف</TableHead>
                        <TableHead className="text-right">المستلم</TableHead>
                        <TableHead className="text-right">المبلغ</TableHead>
                        <TableHead className="text-right">الحالة</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.transfers.rows.map((tr, idx) => (
                        <TableRow key={idx} data-testid={`transfer-row-${idx}`}>
                          <TableCell className="text-right text-sm font-mono">{tr.reference_no || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{tr.date || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{tr.agency_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{tr.employee_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{tr.recipient_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm font-bold text-blue-700">
                            {Number(tr.amount || 0).toLocaleString()} دج
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            <Badge variant={tr.status === 'confirmed' ? 'default' : tr.status === 'pending' ? 'secondary' : 'destructive'}>
                              {tr.status === 'confirmed' ? '✅ مؤكد' : tr.status === 'pending' ? '⏳ معلق' : '❌ ملغى'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* 🆕 Expenses section — out-of-pocket spending logged by the
                employee, with approval status. */}
            {reportData.expenses && reportData.expenses.rows && reportData.expenses.rows.length > 0 && (
              <div className="mt-6 border-2 border-orange-200 rounded-lg p-4 bg-orange-50/30" data-testid="expenses-section">
                <h3 className="text-lg font-bold mb-3 text-orange-900 flex items-center gap-2">
                  🧾 المصاريف ({reportData.expenses.count})
                  <span className="text-sm font-normal text-orange-700 mr-auto">
                    المجموع المعتمد: <b>{Number(reportData.expenses.total || 0).toLocaleString()} دج</b>
                  </span>
                </h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-right">التاريخ</TableHead>
                        <TableHead className="text-right">الوكالة</TableHead>
                        <TableHead className="text-right">الموظف</TableHead>
                        <TableHead className="text-right">الفئة</TableHead>
                        <TableHead className="text-right">الوصف</TableHead>
                        <TableHead className="text-right">المبلغ</TableHead>
                        <TableHead className="text-right">الحالة</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.expenses.rows.map((ex, idx) => {
                        const catLbl = { operational: 'تشغيلي', travel: 'سفر', supplies: 'لوازم', other: 'أخرى' }[ex.category] || ex.category;
                        return (
                        <TableRow key={idx} data-testid={`expense-row-${idx}`}>
                          <TableCell className="text-right text-sm">{ex.date || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{ex.agency_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{ex.employee_name || '—'}</TableCell>
                          <TableCell className="text-right text-sm">{catLbl}</TableCell>
                          <TableCell className="text-right text-sm">{ex.description || '—'}</TableCell>
                          <TableCell className="text-right text-sm font-bold text-orange-700">
                            {Number(ex.amount || 0).toLocaleString()} دج
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            <Badge variant={ex.status === 'approved' ? 'default' : ex.status === 'pending' ? 'secondary' : 'destructive'}>
                              {ex.status === 'approved' ? '✅ معتمد' : ex.status === 'pending' ? '⏳ معلق' : '❌ مرفوض'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 🆕 Send-by-email confirmation dialog. The Print PDF endpoint is
          reused server-side to attach the rendered PDF to a Resend email
          aimed at Agency.manager_email (or an override). */}
      <Dialog
        open={emailDialogOpen}
        onOpenChange={(o) => { if (!o && !emailSending) { setEmailDialogOpen(false); setEmailResult(null); } }}
      >
        <DialogContent className="max-w-md" data-testid="email-report-dialog">
          <DialogHeader>
            <DialogTitle className="text-right text-indigo-800">
              📧 إرسال التقرير بالبريد للمدير
            </DialogTitle>
            <DialogDescription className="text-right pt-2 text-sm leading-relaxed text-gray-700">
              سيتم توليد التقرير بصيغة PDF وإرساله كمرفق إلى بريد المدير المسجَّل في إعدادات الوكالة. يمكنك أيضاً تخصيص بريد آخر للإرسال إليه مرة واحدة.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 pt-1">
            <Label htmlFor="email-override-input" className="text-sm font-semibold">
              بريد إضافي (اختياري — اتركه فارغاً لاستخدام بريد المدير المسجَّل):
            </Label>
            <Input
              id="email-override-input"
              data-testid="email-override-input"
              type="email"
              dir="ltr"
              value={emailOverride}
              onChange={(e) => setEmailOverride(e.target.value)}
              placeholder="manager@example.com"
              disabled={emailSending}
            />
          </div>

          {emailResult && (
            <div
              className={`p-3 rounded-md text-sm ${emailResult.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
              data-testid="email-result-box"
            >
              {emailResult.ok ? '✅ ' : '⚠️ '}{emailResult.message}
              {emailResult.ok && emailResult.to && (
                <div className="text-xs mt-1 text-green-700">المستلم: {emailResult.to}</div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 pt-3">
            <Button
              variant="outline"
              onClick={() => { setEmailDialogOpen(false); setEmailResult(null); }}
              disabled={emailSending}
              data-testid="email-cancel-btn"
            >
              إغلاق
            </Button>
            <Button
              onClick={submitEmailReport}
              disabled={emailSending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="email-send-btn"
            >
              {emailSending ? '⏳ جاري الإرسال...' : '📧 إرسال الآن'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// NEW: Refund Management Component


export default DailyOperationsReports;
